import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useSEO, SEO_PAGES } from "../hooks/useSEO";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput, { validatePhone, formatPhone } from "../components/PhoneInput";

/* ─── Colour palette (matches existing site) ─── */
const C = {
  saffron:"#E8922A", saffronL:"#FDF0D5", saffronD:"#B86A10",
  sage:"#4A7C59",    sageL:"#EAF4EE",    sageD:"#2D5A3D",
  terra:"#B85C38",
  cream:"#FDFAF3",   creamD:"#F5EDD8",
  turmeric:"#F7C948",
  ink:"#2C1810",     inkMid:"#5C3D2E",   inkLight:"#9A7B60",
};

/* ─── Common timezones ─── */
const TIMEZONES = [
  "Asia/Kolkata","Asia/Mumbai","Asia/Delhi",
  "Asia/Dubai","Asia/Singapore","Asia/Tokyo",
  "Europe/London","Europe/Paris","Europe/Berlin",
  "America/New_York","America/Chicago","America/Los_Angeles",
  "America/Toronto","Australia/Sydney","Pacific/Auckland",
];

/* ─── Step indicator ─── */
function Steps({ current }) {
  const steps = ["Your Details","Disclaimer","Payment"];
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
      gap:0, marginBottom:36 }}>
      {steps.map((s,i) => (
        <div key={s} style={{ display:"flex", alignItems:"center" }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
            <div style={{
              width:34, height:34, borderRadius:"50%",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontWeight:700, fontSize:13, fontFamily:"'Inter',sans-serif",
              background: i < current
                ? `linear-gradient(135deg,${C.sage},${C.sageD})`
                : i === current
                  ? `linear-gradient(135deg,${C.saffron},${C.terra})`
                  : C.creamD,
              color: i <= current ? "#fff" : C.inkLight,
              boxShadow: i === current ? `0 4px 14px rgba(232,146,42,0.38)` : "none",
              transition:"all 0.3s",
            }}>
              {i < current ? "✓" : i+1}
            </div>
            <span style={{ fontSize:10, fontWeight:600, color: i===current ? C.saffronD : C.inkLight,
              fontFamily:"'Inter',sans-serif", whiteSpace:"nowrap" }}>{s}</span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ width:"clamp(20px,6vw,48px)", height:2, margin:"0 4px", marginBottom:20,
              background: i < current
                ? `linear-gradient(90deg,${C.sage},${C.saffron})`
                : C.creamD,
              transition:"all 0.3s" }}/>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── Styled input ─── */
function SInput({ label, value, onChange, placeholder, error, type="text", maxLength, required }) {
  return (
    <div>
      <label style={{ fontSize:12, fontWeight:600, color:C.inkMid,
        fontFamily:"'Inter',sans-serif", marginBottom:6, display:"block" }}>
        {label}{required && <span style={{ color:C.terra }}> *</span>}
      </label>
      <input type={type} placeholder={placeholder} value={value}
        onChange={onChange} maxLength={maxLength}
        style={{ width:"100%", padding:"12px 16px", borderRadius:12,
          boxSizing:"border-box",
          border:`1.5px solid ${error ? "#EF4444" : C.creamD}`,
          fontSize:14, fontFamily:"'Inter',sans-serif",
          outline:"none", background:C.cream, color:C.ink, transition:"border 0.2s" }}
        onFocus={e => e.target.style.border=`1.5px solid ${error?"#EF4444":C.saffron}`}
        onBlur={e  => e.target.style.border=`1.5px solid ${error?"#EF4444":C.creamD}`}
      />
      {error && <p style={{ color:"#EF4444", fontSize:12, marginTop:4,
        fontFamily:"'Inter',sans-serif" }}>{error}</p>}
    </div>
  );
}

function SSelect({ label, value, onChange, options, error, required }) {
  return (
    <div>
      <label style={{ fontSize:12, fontWeight:600, color:C.inkMid,
        fontFamily:"'Inter',sans-serif", marginBottom:6, display:"block" }}>
        {label}{required && <span style={{ color:C.terra }}> *</span>}
      </label>
      <select value={value} onChange={onChange}
        style={{ width:"100%", padding:"12px 16px", borderRadius:12,
          border:`1.5px solid ${error ? "#EF4444" : C.creamD}`,
          fontSize:14, fontFamily:"'Inter',sans-serif",
          outline:"none", background:C.cream, color:C.ink,
          appearance:"auto", cursor:"pointer" }}>
        {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
      </select>
      {error && <p style={{ color:"#EF4444", fontSize:12, marginTop:4,
        fontFamily:"'Inter',sans-serif" }}>{error}</p>}
    </div>
  );
}

/* ─── Lotus SVG (decoration) ─── */
const Lotus = ({ size=32, color=C.saffron }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    {[0,30,60,-30,-60,90].map((r,i) => (
      <ellipse key={i} cx="40" cy="50" rx="7" ry="16"
        fill={color} opacity={i>3?0.45:i>1?0.7:0.95}
        transform={`rotate(${r} 40 50)`}/>
    ))}
    <circle cx="40" cy="44" r="7" fill={C.turmeric}/>
    <rect x="38" y="58" width="4" height="10" rx="2" fill={C.sage} opacity="0.55"/>
  </svg>
);

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════ */
export default function RequestPage() {
  useSEO(SEO_PAGES.request);
  const navigate   = useNavigate();
  const [step, setStep]   = useState(0);   // 0=form, 1=disclaimer, 2=payment
  const [doctors, setDoctors]     = useState([]);
  const [doctorProfiles, setDoctorProfiles] = useState([]);
  const [disclaimers, setDisclaimers] = useState({
    Online: "", Appointment: "", Enquiry: "",
  });
  const [consultFee, setConsultFee] = useState(500);
  const [submitting, setSubmitting] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [success, setSuccess]   = useState(false);
  const [refNumber, setRefNumber] = useState("");

  const todayStr = new Date().toISOString().slice(0,10);
  const detectedTZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

  // ── form state MUST come before any computed values that read form ──
  const [form, setForm] = useState({
    name:"", phone:{ countryCode: "+91", number: "" }, age:"", gender:"",
    type:"Appointment",
    consultationType:"In-Person",
    doctorId:"", doctorName:"",
    doctorProfileId:"", doctorProfileName:"",
    appointmentDate:"", preferredTime:"",
    timezone: detectedTZ,
    message:"",
  });
  const [errors, setErrors] = useState({});

  /* ── Min-time helper: if today selected, earliest = now + 6 h ── */
  const getMinTime = (selectedDate) => {
    if (!selectedDate || selectedDate !== todayStr) return null;
    const earliest = new Date(Date.now() + 6 * 60 * 60 * 1000);
    const hh = String(earliest.getHours()).padStart(2, "0");
    const mm = String(earliest.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };
  // minTime derives from form — safe now that form is declared above
  const minTime = getMinTime(form.appointmentDate);

  /* ── IST preview: convert patient's selected time+tz → IST ──
     Returns a formatted string like "03 Jan — 08:30 PM IST" or null. */
  const getISTPreview = (date, time, tz) => {
    if (!date || !time || !tz || tz === "Asia/Kolkata") return null;
    try {
      const dt = new Date(`${date}T${time}:00`);
      const inPatientTZ = new Date(dt.toLocaleString("en-US", { timeZone: tz }));
      const inUTC       = new Date(dt.toLocaleString("en-US", { timeZone: "UTC" }));
      const offsetMs    = inUTC - inPatientTZ;
      const actualUTC   = new Date(dt.getTime() + offsetMs);
      return actualUTC.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit", minute: "2-digit", hour12: true,
        day: "2-digit", month: "short",
      }).replace(",", " —") + " IST";
    } catch { return null; }
  };

  /* ── Fetch doctors list, disclaimer, fee on mount ── */
  useEffect(() => {
    // Fetch public doctor list (for online consultations)
    api.get("/doctor/list").then(r => setDoctors(r.data || [])).catch(() => {});
    // Fetch doctor profiles (for in-person appointments — "Our Doctors" page)
    api.get("/doctor-profiles").then(r => setDoctorProfiles((r.data?.data || []).filter(d => d.isVisible))).catch(() => {});
    // Fetch per-type disclaimers
    const discKeys = [
      ["disclaimer_online",      "Online"],
      ["disclaimer_appointment", "Appointment"],
      ["disclaimer_enquiry",     "Enquiry"],
    ];
    Promise.all(discKeys.map(([key]) => api.get(`/settings/${key}`).catch(() => ({ data: "" }))))
      .then(results => {
        const map = {};
        discKeys.forEach(([, type], i) => { map[type] = results[i].data || ""; });
        // Fallback: if online disclaimer not set yet, try legacy key
        if (!map.Online) {
          api.get("/settings/online_consultation_disclaimer")
            .then(r => { if (r.data) setDisclaimers(p => ({ ...p, Online: r.data })); })
            .catch(() => {});
        }
        setDisclaimers(map);
      });
    // Fetch consultation fee
    api.get("/settings/consultation_fee")
      .then(r => { if (r.data) setConsultFee(Number(r.data)); })
      .catch(() => {});
  }, []);

  const set = (key, value) => {
    setForm(p => ({ ...p, [key]:value }));
    if (errors[key]) setErrors(p => ({ ...p, [key]:"" }));
  };

  const isOnline = form.type === "Online";
  // Pick the right disclaimer for the selected request type
  const activeDisclaimer = disclaimers[form.type] || "";

  /* ── Validation ── */
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    else if (!/^[a-zA-Z\s'.\\-]+$/.test(form.name.trim())) e.name = "Letters only";
    const phoneErr = validatePhone(form.phone, true);
    if (phoneErr) e.phone = phoneErr;
    if (!form.message.trim()) e.message = "Please describe your concern";
    if (form.type !== "Enquiry") {
      if (!form.appointmentDate) e.appointmentDate = "Please select a date";
      else if (form.appointmentDate < todayStr) e.appointmentDate = "Date cannot be in the past";
    }
    if (isOnline) {
      if (!form.preferredTime) {
        e.preferredTime = "Please select a time";
      } else if (form.appointmentDate === todayStr) {
        const mt = getMinTime(todayStr);
        if (mt && form.preferredTime < mt) {
          const earliest = new Date(Date.now() + 6 * 60 * 60 * 1000);
          const h = earliest.getHours();
          const m = String(earliest.getMinutes()).padStart(2, "0");
          const ampm = h >= 12 ? "PM" : "AM";
          const h12 = h % 12 || 12;
          e.preferredTime = `For today, please choose a time after ${h12}:${m} ${ampm} (6 hours from now)`;
        }
      }
      if (!form.doctorId) e.doctorId = "Please select a doctor";
    }
    if (form.type === "Appointment" && doctorProfiles.length > 0 && !form.doctorProfileId) {
      e.doctorProfileId = "Please select a doctor";
    }
    return e;
  };

  /* ── Step 0 → Step 1 ── */
  const goToDisclaimer = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStep(1);
    window.scrollTo({ top: 0, behavior:"smooth" });
  };

  /* ── Step 1 → Step 2 (online) or submit directly (in-person) ── */
  const acceptDisclaimer = async () => {
    if (isOnline) {
      setStep(2);
      window.scrollTo({ top: 0, behavior:"smooth" });
    } else {
      // In-person: no payment needed, submit directly
      setSubmitting(true);
      try {
        const res = await api.post("/requests", {
          ...form,
          phone: formatPhone(form.phone),
          age: form.age ? Number(form.age) : undefined,
        });
        setRefNumber(res.data?._id?.slice(-8).toUpperCase() || res.data?.refNumber || "—");
        setSuccess(true);
      } finally {
        setSubmitting(false);
      }
    }
  };

  /* ── Razorpay payment flow ── */
  const handlePayment = async () => {
    setPaymentLoading(true);
    try {
      // 1. Create order on backend
      const orderRes = await api.post("/payment/create-order", { amount: consultFee });
      const { orderId, amount, currency, keyId } = orderRes.data;

      // 2. Load Razorpay script if not already loaded
      await loadRazorpayScript();

      // 3. Open Razorpay checkout
      const options = {
        key:         keyId,
        amount:      amount,
        currency:    currency,
        name:        "Online Consultation",
        description: `Consultation with Dr. ${form.doctorName || ""}`,
        order_id:    orderId,
        prefill: {
          name:    form.name,
          contact: formatPhone(form.phone),
        },
        theme: { color: C.saffron },
        handler: async (response) => {
          // 4. Verify on backend
          const verifyRes = await api.post("/payment/verify", {
            razorpay_order_id:   response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature:  response.razorpay_signature,
          });
          if (verifyRes.data.verified) {
            // 5. Auto-submit the request
            const bookingRes = await api.post("/requests", {
              ...form,
              phone:          formatPhone(form.phone),
              type:           "Online",
              age:            form.age ? Number(form.age) : undefined,
              paymentId:      response.razorpay_payment_id,
              razorpayOrderId:response.razorpay_order_id,
              fees:           consultFee,
              paymentStatus:  "paid",
            });
            setRefNumber(bookingRes.data?._id?.slice(-8).toUpperCase() || bookingRes.data?.refNumber || response.razorpay_payment_id?.slice(-8).toUpperCase() || "—");
            setSuccess(true);
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: () => setPaymentLoading(false),
        },
      };

      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        setPaymentLoading(false);
        alert("Payment failed. Please try again.");
      });
      rzp.open();

    } catch (err) {
      console.error("Payment error:", err);
      alert(err.response?.data?.message || "Payment could not be initiated. Please try again.");
      setPaymentLoading(false);
    }
  };

  return (
    <div style={{ background:C.cream, minHeight:"100vh", paddingTop:72, overflowX:"hidden" }}>

      {/* ── Hero ── */}
      <section style={{
        position:"relative", padding:"52px 24px 44px",
        background:`linear-gradient(145deg,${C.cream} 0%,${C.creamD} 45%,${C.saffronL} 100%)`,
        overflow:"hidden", textAlign:"center"
      }}>
        <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.5 }} style={{ position:"relative", zIndex:2 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginBottom:12,
            background:C.saffronL, border:`1px solid ${C.saffron}40`,
            borderRadius:20, padding:"4px 16px" }}>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.13em",
              color:C.saffronD, textTransform:"uppercase",
              fontFamily:"'Inter',sans-serif" }}>Book Appointment</span>
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(28px,4.5vw,50px)", fontWeight:800,
            color:C.ink, marginBottom:10 }}>
            {step === 0 && "Book a Consultation"}
            {step === 1 && "Important Notice"}
            {step === 2 && "Complete Payment"}
          </h1>
          <div style={{ display:"flex", alignItems:"center",
            justifyContent:"center", gap:14, marginBottom:8 }}>
            <div style={{ height:1, width:50, background:`linear-gradient(90deg,transparent,${C.saffron})` }}/>
            <Lotus size={28} color={C.saffron}/>
            <div style={{ height:1, width:50, background:`linear-gradient(90deg,${C.saffron},transparent)` }}/>
          </div>
        </motion.div>
      </section>

      {/* ── Form card ── */}
      <section style={{ padding:"40px 24px 72px" }}>
        <div style={{ maxWidth:580, margin:"0 auto" }}>
          <AnimatePresence mode="wait">

            {/* ═══ STEP 0 — DETAILS FORM ═══ */}
            {step === 0 && (
              <motion.div key="step0"
                initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
                exit={{ opacity:0, y:-20 }}>

                <Steps current={0}/>

                <div style={{ background:"#fff", borderRadius:24, padding:"36px 32px",
                  boxShadow:"0 12px 50px rgba(0,0,0,0.09)",
                  border:`1px solid ${C.creamD}` }}>

                  {/* Type toggle */}
                  <div style={{ display:"flex", gap:8, marginBottom:28,
                    background:C.cream, borderRadius:14, padding:4 }}>
                    {[
                      { v:"Appointment", label:"🏥 In-Person" },
                      { v:"Online",      label:"💻 Online Consult" },
                      { v:"Enquiry",     label:"💬 Enquiry" },
                    ].map(t => (
                      <motion.button key={t.v} type="button"
                        onClick={() => set("type", t.v)}
                        whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
                        style={{
                          flex:1, padding:"11px 4px", borderRadius:11,
                          fontWeight:600, fontSize:12, cursor:"pointer",
                          border:"none", fontFamily:"'Inter',sans-serif",
                          transition:"all 0.2s",
                          background: form.type===t.v
                            ? `linear-gradient(135deg,${C.saffron},${C.terra})`
                            : "transparent",
                          color: form.type===t.v ? "#fff" : C.inkLight,
                          boxShadow: form.type===t.v
                            ? `0 4px 16px rgba(232,146,42,0.30)` : "none",
                        }}>{t.label}</motion.button>
                    ))}
                  </div>

                  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

                    {/* Name + Phone */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(220px,100%),1fr))", gap:12 }}>
                      <SInput label="Full Name" value={form.name} required
                        placeholder="Your full name" error={errors.name}
                        onChange={e => set("name", e.target.value.replace(/[^a-zA-Z\s'.\\-]/g, ""))}/>
                      <PhoneInput
                        label="Phone"
                        required
                        value={form.phone}
                        onChange={v => set("phone", v)}
                        error={errors.phone}
                      />
                    </div>

                    {/* Age + Gender */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(180px,100%),1fr))", gap:12 }}>
                      <SInput label="Age" value={form.age} placeholder="Years"
                        onChange={e => set("age", e.target.value.replace(/\D/g,""))}/>
                      <SSelect label="Gender" value={form.gender}
                        onChange={e => set("gender", e.target.value)}
                        options={[
                          { value:"", label:"— Select —" },
                          { value:"Male",   label:"Male" },
                          { value:"Female", label:"Female" },
                          { value:"Other",  label:"Other" },
                        ]}/>
                    </div>

                    {/* Appointment: doctor picker from DoctorProfiles */}
                    {form.type === "Appointment" && doctorProfiles.length > 0 && (
                      <AnimatePresence>
                        <motion.div initial={{ opacity:0, height:0 }}
                          animate={{ opacity:1, height:"auto" }}
                          exit={{ opacity:0, height:0 }}>
                          <SSelect label="Select Doctor" value={form.doctorProfileId}
                            required error={errors.doctorProfileId}
                            onChange={e => {
                              const doc = doctorProfiles.find(d => d._id === e.target.value);
                              set("doctorProfileId", e.target.value);
                              set("doctorProfileName", doc?.name || "");
                            }}
                            options={[
                              { value:"", label:"— Choose a doctor —" },
                              ...doctorProfiles.map(d => ({
                                value: d._id,
                                label: `Dr. ${d.name}${d.qualification ? ` — ${d.qualification}` : ""}${d.timing ? ` · ${d.timing}` : ""}`,
                              }))
                            ]}/>
                          {form.doctorProfileId && (() => {
                            const doc = doctorProfiles.find(d => d._id === form.doctorProfileId);
                            return doc ? (
                              <div style={{ marginTop:8, background:"#F0FDF4", borderRadius:12,
                                padding:"12px 16px", border:"1px solid #BBF7D0",
                                display:"flex", alignItems:"center", gap:12 }}>
                                <span style={{ fontSize:24 }}>👨‍⚕️</span>
                                <div>
                                  <p style={{ margin:0, fontSize:13, fontWeight:700, color:"#166534",
                                    fontFamily:"'Inter',sans-serif" }}>Dr. {doc.name}</p>
                                  {doc.qualification && <p style={{ margin:0, fontSize:11,
                                    color:"#4A7C59", fontFamily:"'Inter',sans-serif" }}>{doc.qualification}</p>}
                                  {doc.timing && <p style={{ margin:0, fontSize:11, color:"#4A7C59",
                                    fontFamily:"'Inter',sans-serif" }}>🕐 {doc.timing}</p>}
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </motion.div>
                      </AnimatePresence>
                    )}

                    {/* Online: doctor + consult type */}
                    {isOnline && (
                      <AnimatePresence>
                        <motion.div initial={{ opacity:0, height:0 }}
                          animate={{ opacity:1, height:"auto" }}
                          exit={{ opacity:0, height:0 }}
                          style={{ display:"flex", flexDirection:"column", gap:12 }}>

                          <SSelect label="Select Doctor" value={form.doctorId}
                            required error={errors.doctorId}
                            onChange={e => {
                              const doc = doctors.find(d => d._id === e.target.value);
                              set("doctorId", e.target.value);
                              set("doctorName", doc?.name || "");
                            }}
                            options={[
                              { value:"", label:"— Choose a doctor —" },
                              ...doctors.map(d => ({ value:d._id, label:`Dr. ${d.name} — ${d.qualification||""}` }))
                            ]}/>

                          <SSelect label="Consultation Type" value={form.consultationType}
                            onChange={e => set("consultationType", e.target.value)}
                            options={[
                              { value:"Online Video", label:"📹 Online Video Call" },
                              { value:"Phone",        label:"📞 Phone Consultation" },
                            ]}/>

                          {/* Fee notice */}
                          <div style={{ background:C.saffronL, borderRadius:12,
                            padding:"12px 16px", border:`1px solid ${C.saffron}30`,
                            display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ fontSize:20 }}>💳</span>
                            <div>
                              <p style={{ margin:0, fontSize:13, fontWeight:700,
                                color:C.saffronD, fontFamily:"'Inter',sans-serif" }}>
                                Consultation Fee: ₹{consultFee}
                              </p>
                              <p style={{ margin:0, fontSize:11, color:C.inkLight,
                                fontFamily:"'Inter',sans-serif" }}>
                                Secure payment via Razorpay
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    )}

                    {/* Date + Time */}
                    {form.type !== "Enquiry" && (
                      <div style={{ display:"grid",
                        gridTemplateColumns: isOnline ? "repeat(auto-fit,minmax(min(200px,100%),1fr))" : "1fr",
                        gap:12 }}>
                        <div>
                          <label style={{ fontSize:12, fontWeight:600, color:C.inkMid,
                            fontFamily:"'Inter',sans-serif", marginBottom:6, display:"block" }}>
                            Preferred Date <span style={{ color:C.terra }}>*</span>
                          </label>
                          <input type="date" min={todayStr} value={form.appointmentDate}
                            onChange={e => { set("appointmentDate", e.target.value); set("preferredTime", ""); }}
                            style={{ width:"100%", padding:"12px 16px", borderRadius:12,
                              border:`1.5px solid ${errors.appointmentDate?"#EF4444":C.creamD}`,
                              fontSize:14, fontFamily:"'Inter',sans-serif",
                              outline:"none", background:C.cream, color:C.ink,
                              boxSizing:"border-box" }}
                            onFocus={e=>e.target.style.border=`1.5px solid ${C.saffron}`}
                            onBlur={e=>e.target.style.border=`1.5px solid ${errors.appointmentDate?"#EF4444":C.creamD}`}
                          />
                          {errors.appointmentDate && (
                            <p style={{ color:"#EF4444", fontSize:12, marginTop:4,
                              fontFamily:"'Inter',sans-serif" }}>{errors.appointmentDate}</p>
                          )}
                        </div>

                        {isOnline && (
                          <div>
                            <label style={{ fontSize:12, fontWeight:600, color:C.inkMid,
                              fontFamily:"'Inter',sans-serif", marginBottom:6, display:"block" }}>
                              Preferred Time <span style={{ color:C.terra }}>*</span>
                            </label>
                            <input type="time" value={form.preferredTime}
                              min={minTime || undefined}
                              onChange={e => set("preferredTime", e.target.value)}
                              style={{ width:"100%", padding:"12px 16px", borderRadius:12,
                                border:`1.5px solid ${errors.preferredTime?"#EF4444":C.creamD}`,
                                fontSize:14, fontFamily:"'Inter',sans-serif",
                                outline:"none", background:C.cream, color:C.ink,
                                boxSizing:"border-box" }}
                              onFocus={e=>e.target.style.border=`1.5px solid ${C.saffron}`}
                              onBlur={e=>e.target.style.border=`1.5px solid ${errors.preferredTime?"#EF4444":C.creamD}`}
                            />
                            {minTime && !errors.preferredTime && (
                              <p style={{ fontSize:11, color:C.inkLight, marginTop:4,
                                fontFamily:"'Inter',sans-serif" }}>
                                ⏰ Today's earliest slot: <strong>{(() => {
                                  const [h, m] = minTime.split(":").map(Number);
                                  const ampm = h >= 12 ? "PM" : "AM";
                                  return `${h % 12 || 12}:${String(m).padStart(2,"0")} ${ampm}`;
                                })()}</strong> (6 hrs from now)
                              </p>
                            )}
                            {errors.preferredTime && (
                              <p style={{ color:"#EF4444", fontSize:12, marginTop:4,
                                fontFamily:"'Inter',sans-serif" }}>{errors.preferredTime}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Timezone (online only) */}
                    {isOnline && (
                      <div>
                        <SSelect label="Your Timezone" value={form.timezone}
                          onChange={e => set("timezone", e.target.value)}
                          options={TIMEZONES.map(tz => ({ value:tz, label:tz }))}/>
                        {(() => {
                          const ist = getISTPreview(form.appointmentDate, form.preferredTime, form.timezone);
                          return ist ? (
                            <div style={{ marginTop:8, padding:"8px 12px", borderRadius:10,
                              background:"#FFF7ED", border:"1px solid #FDE68A",
                              display:"flex", alignItems:"center", gap:8 }}>
                              <span style={{ fontSize:16 }}>🇮🇳</span>
                              <p style={{ fontSize:12, color:"#92400E", fontFamily:"'Inter',sans-serif",
                                margin:0 }}>
                                That's <strong>{ist}</strong> for the doctor in India
                              </p>
                            </div>
                          ) : (
                            form.appointmentDate && form.preferredTime && form.timezone === "Asia/Kolkata" ? (
                              <div style={{ marginTop:8, padding:"8px 12px", borderRadius:10,
                                background:"#F0FDF4", border:"1px solid #BBF7D0",
                                display:"flex", alignItems:"center", gap:8 }}>
                                <span style={{ fontSize:16 }}>🇮🇳</span>
                                <p style={{ fontSize:12, color:"#166534", fontFamily:"'Inter',sans-serif",
                                  margin:0 }}>
                                  You're already in IST — no conversion needed
                                </p>
                              </div>
                            ) : null
                          );
                        })()}
                      </div>
                    )}

                    {/* Message */}
                    <div>
                      <label style={{ fontSize:12, fontWeight:600, color:C.inkMid,
                        fontFamily:"'Inter',sans-serif", marginBottom:6, display:"block" }}>
                        {form.type === "Enquiry" ? "Your Message" : "Health Concern"} <span style={{ color:C.terra }}>*</span>
                      </label>
                      <textarea rows={3}
                        placeholder={form.type === "Enquiry"
                          ? "Write your enquiry…"
                          : "Describe your symptoms or reason for consultation…"}
                        value={form.message}
                        onChange={e => set("message", e.target.value)}
                        style={{ width:"100%", padding:"12px 16px", borderRadius:12,
                          border:`1.5px solid ${errors.message?"#EF4444":C.creamD}`,
                          fontSize:14, fontFamily:"'Merriweather',serif", resize:"vertical",
                          outline:"none", background:C.cream, color:C.ink, lineHeight:1.65,
                          boxSizing:"border-box" }}
                        onFocus={e=>e.target.style.border=`1.5px solid ${errors.message?"#EF4444":C.saffron}`}
                        onBlur={e=>e.target.style.border=`1.5px solid ${errors.message?"#EF4444":C.creamD}`}
                      />
                      {errors.message && (
                        <p style={{ color:"#EF4444", fontSize:12, marginTop:4,
                          fontFamily:"'Inter',sans-serif" }}>{errors.message}</p>
                      )}
                    </div>

                    {/* Submit */}
                    <motion.button type="button" onClick={goToDisclaimer}
                      whileHover={{ scale:1.02, boxShadow:"0 14px 34px rgba(232,146,42,0.45)" }}
                      whileTap={{ scale:0.98 }}
                      style={{ width:"100%", padding:"15px 0", borderRadius:14, border:"none",
                        background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                        color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer",
                        fontFamily:"'Inter',sans-serif",
                        boxShadow:`0 8px 26px rgba(232,146,42,0.35)` }}>
                      Continue →
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ═══ STEP 1 — DISCLAIMER ═══ */}
            {step === 1 && (
              <motion.div key="step1"
                initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
                exit={{ opacity:0, y:-20 }}>

                <Steps current={1}/>

                <div style={{ background:"#fff", borderRadius:24, padding:"36px 32px",
                  boxShadow:"0 12px 50px rgba(0,0,0,0.09)",
                  border:`1px solid ${C.creamD}` }}>

                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
                    <div style={{ width:44, height:44, borderRadius:12,
                      background:`linear-gradient(135deg,${C.saffronL},${C.creamD})`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:22, flexShrink:0 }}>📋</div>
                    <div>
                      <p style={{ margin:0, fontWeight:700, color:C.ink,
                        fontFamily:"'Playfair Display',serif", fontSize:16 }}>
                        Please Read Carefully
                      </p>
                      <p style={{ margin:0, fontSize:12, color:C.inkLight,
                        fontFamily:"'Inter',sans-serif" }}>
                        This notice must be acknowledged before proceeding
                      </p>
                    </div>
                  </div>

                  {/* Disclaimer text */}
                  <div style={{ background:C.saffronL, borderRadius:14, padding:"20px 22px",
                    border:`1px solid ${C.saffron}25`, marginBottom:24,
                    maxHeight:280, overflowY:"auto" }}>
                    {activeDisclaimer ? (
                      <p style={{ margin:0, fontSize:14, color:C.inkMid, lineHeight:1.8,
                        fontFamily:"'Merriweather',serif", whiteSpace:"pre-wrap" }}>
                        {activeDisclaimer}
                      </p>
                    ) : (
                      <p style={{ margin:0, fontSize:14, color:C.inkMid, lineHeight:1.8,
                        fontFamily:"'Merriweather',serif" }}>
                        By proceeding, you acknowledge that this consultation is for informational
                        purposes and does not replace emergency medical care. Our doctors will
                        contact you within 24 hours of booking to confirm your appointment time.
                        The consultation fee is non-refundable once the appointment is confirmed.
                        Please ensure you have a stable internet connection for video consultations.
                        All medical information shared is kept strictly confidential.
                      </p>
                    )}
                  </div>

                  {/* Booking summary */}
                  <div style={{ background:C.sageL, borderRadius:14, padding:"16px 18px",
                    border:`1px solid ${C.sage}20`, marginBottom:24 }}>
                    <p style={{ margin:"0 0 10px", fontSize:12, fontWeight:700,
                      color:C.sageD, fontFamily:"'Inter',sans-serif",
                      textTransform:"uppercase", letterSpacing:"0.1em" }}>
                      Your Booking Summary
                    </p>
                    {[
                      ["Name",    form.name],
                      ["Phone",   formatPhone(form.phone)],
                      form.type === "Appointment" && form.doctorProfileName && ["Doctor", `Dr. ${form.doctorProfileName}`],
                      isOnline && ["Doctor",  `Dr. ${form.doctorName}`],
                      isOnline && ["Type",    form.consultationType],
                      form.appointmentDate && ["Date", form.appointmentDate],
                      isOnline && form.preferredTime && ["Time", (() => {
                        const ist = getISTPreview(form.appointmentDate, form.preferredTime, form.timezone);
                        const base = `${form.preferredTime} (${form.timezone})`;
                        return ist ? `${base} → ${ist}` : base;
                      })()],
                      isOnline && ["Fee", `₹${consultFee}`],
                    ].filter(Boolean).map(([k,v]) => (
                      <div key={k} style={{ display:"flex", justifyContent:"space-between",
                        padding:"4px 0", borderBottom:`1px solid ${C.sage}15` }}>
                        <span style={{ fontSize:12, color:C.inkLight,
                          fontFamily:"'Inter',sans-serif" }}>{k}</span>
                        <span style={{ fontSize:12, fontWeight:600, color:C.ink,
                          fontFamily:"'Inter',sans-serif" }}>{v}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ display:"flex", gap:12 }}>
                    <button type="button" onClick={() => setStep(0)}
                      style={{ flex:1, padding:"13px 0", borderRadius:12, border:`1.5px solid ${C.creamD}`,
                        background:"transparent", color:C.inkMid, fontWeight:600,
                        fontSize:14, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      ← Go Back
                    </button>
                    <motion.button type="button"
                      onClick={acceptDisclaimer} disabled={submitting}
                      whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
                      style={{ flex:2, padding:"13px 0", borderRadius:12, border:"none",
                        background: submitting ? C.creamD
                          : `linear-gradient(135deg,${C.sage},${C.sageD})`,
                        color: submitting ? C.inkLight : "#fff",
                        fontWeight:700, fontSize:14, cursor: submitting ? "not-allowed" : "pointer",
                        fontFamily:"'Inter',sans-serif",
                        boxShadow: submitting ? "none" : `0 6px 20px rgba(74,124,89,0.35)` }}>
                      {submitting
                        ? "Submitting…"
                        : isOnline
                          ? "I Agree — Proceed to Payment →"
                          : "I Agree — Submit Request ✓"}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ═══ STEP 2 — PAYMENT (online only) ═══ */}
            {step === 2 && !success && (
              <motion.div key="step2"
                initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
                exit={{ opacity:0, y:-20 }}>

                <Steps current={2}/>

                <div style={{ background:"#fff", borderRadius:24, padding:"36px 32px",
                  boxShadow:"0 12px 50px rgba(0,0,0,0.09)",
                  border:`1px solid ${C.creamD}`, textAlign:"center" }}>

                  <div style={{ width:80, height:80, borderRadius:"50%", margin:"0 auto 20px",
                    background:`linear-gradient(135deg,${C.saffronL},${C.creamD})`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:38 }}>💳</div>

                  <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22,
                    color:C.ink, margin:"0 0 8px" }}>
                    Complete Your Payment
                  </h2>
                  <p style={{ fontSize:14, color:C.inkLight, margin:"0 0 24px",
                    fontFamily:"'Merriweather',serif" }}>
                    Your consultation will be confirmed after successful payment.
                  </p>

                  {/* Amount display */}
                  <div style={{ background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                    borderRadius:16, padding:"24px 32px", marginBottom:24 }}>
                    <p style={{ margin:"0 0 4px", fontSize:12, color:"rgba(255,255,255,0.75)",
                      fontFamily:"'Inter',sans-serif" }}>CONSULTATION FEE</p>
                    <p style={{ margin:0, fontSize:42, fontWeight:800,
                      color:"#fff", fontFamily:"'Playfair Display',serif",
                      lineHeight:1 }}>₹{consultFee}</p>
                    <p style={{ margin:"8px 0 0", fontSize:12, color:"rgba(255,255,255,0.75)",
                      fontFamily:"'Inter',sans-serif" }}>
                      {form.consultationType} · Dr. {form.doctorName}
                    </p>
                  </div>

                  <div style={{ display:"flex", alignItems:"center", gap:8,
                    justifyContent:"center", marginBottom:24,
                    padding:"10px 16px", background:C.sageL,
                    borderRadius:10, border:`1px solid ${C.sage}20` }}>
                    <span style={{ fontSize:16 }}>🔒</span>
                    <span style={{ fontSize:12, color:C.sageD,
                      fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
                      Secured by Razorpay · 256-bit SSL Encryption
                    </span>
                  </div>

                  <div style={{ display:"flex", gap:12 }}>
                    <button type="button" onClick={() => setStep(1)}
                      style={{ flex:1, padding:"13px 0", borderRadius:12,
                        border:`1.5px solid ${C.creamD}`, background:"transparent",
                        color:C.inkMid, fontWeight:600, fontSize:14,
                        cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      ← Back
                    </button>
                    <motion.button type="button"
                      onClick={handlePayment} disabled={paymentLoading}
                      whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
                      style={{ flex:2, padding:"14px 0", borderRadius:12, border:"none",
                        background: paymentLoading ? C.creamD
                          : `linear-gradient(135deg,${C.saffron},${C.terra})`,
                        color: paymentLoading ? C.inkLight : "#fff",
                        fontWeight:700, fontSize:15, cursor: paymentLoading ? "not-allowed" : "pointer",
                        fontFamily:"'Inter',sans-serif",
                        boxShadow: paymentLoading ? "none" : `0 8px 26px rgba(232,146,42,0.38)` }}>
                      {paymentLoading ? "Opening payment…" : `Pay ₹${consultFee} →`}
                    </motion.button>
                  </div>

                </div>
              </motion.div>
            )}

            {/* ═══ SUCCESS ═══ */}
            {success && (
              <motion.div key="success"
                initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }}
                transition={{ duration:0.5 }}>
                <div style={{ background:"#fff", borderRadius:24,
                  boxShadow:"0 12px 50px rgba(0,0,0,0.09)",
                  border:`1px solid ${C.creamD}`, overflow:"hidden" }}>

                  {/* ── Green header banner ── */}
                  <div style={{
                    background:`linear-gradient(135deg,${C.sageD} 0%,${C.sage} 100%)`,
                    padding:"40px 32px 36px", textAlign:"center", position:"relative",
                    overflow:"hidden",
                  }}>
                    {/* Background lotus watermark */}
                    <div style={{ position:"absolute", right:-40, top:-40, opacity:0.07, pointerEvents:"none" }}>
                      <Lotus size={200} color="#fff"/>
                    </div>
                    <motion.div
                      initial={{ scale:0, rotate:-15 }}
                      animate={{ scale:1, rotate:0 }}
                      transition={{ type:"spring", stiffness:220, damping:18, delay:0.1 }}
                      style={{ width:76, height:76, borderRadius:"50%", margin:"0 auto 18px",
                        background:"rgba(255,255,255,0.15)",
                        border:"2.5px solid rgba(255,255,255,0.4)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:36 }}>
                      ✅
                    </motion.div>
                    <motion.h2
                      initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                      transition={{ delay:0.25 }}
                      style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(20px,3.5vw,26px)",
                        color:"#fff", margin:"0 0 8px", fontWeight:800 }}>
                      {isOnline ? "Consultation Booked!" : "Request Submitted!"}
                    </motion.h2>
                    <motion.p
                      initial={{ opacity:0 }} animate={{ opacity:1 }}
                      transition={{ delay:0.35 }}
                      style={{ fontSize:14, color:"rgba(255,255,255,0.78)", margin:0,
                        fontFamily:"'Merriweather',serif" }}>
                      {isOnline
                        ? "Payment confirmed. We'll reach you within 24 hours."
                        : "We'll confirm your appointment within 24 hours."}
                    </motion.p>
                  </div>

                  {/* ── Reference number ── */}
                  <motion.div
                    initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                    transition={{ delay:0.4 }}
                    style={{ margin:"28px 32px 0",
                      background:C.saffronL, borderRadius:16,
                      border:`1.5px dashed ${C.saffron}60`,
                      padding:"20px 24px", textAlign:"center" }}>
                    <p style={{ margin:"0 0 6px", fontSize:11, fontWeight:700,
                      letterSpacing:"0.14em", textTransform:"uppercase",
                      color:C.saffronD, fontFamily:"'Inter',sans-serif" }}>
                      Your Reference Number
                    </p>
                    <p style={{ margin:0, fontSize:28, fontWeight:800,
                      fontFamily:"'Playfair Display',serif", color:C.ink,
                      letterSpacing:"0.08em" }}>
                      #{refNumber}
                    </p>
                    <p style={{ margin:"6px 0 0", fontSize:11, color:C.inkLight,
                      fontFamily:"'Inter',sans-serif" }}>
                      Save this number to track your request
                    </p>
                  </motion.div>

                  {/* ── Booking summary ── */}
                  <motion.div
                    initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                    transition={{ delay:0.5 }}
                    style={{ margin:"20px 32px 0",
                      background:C.cream, borderRadius:14,
                      border:`1px solid ${C.creamD}`, padding:"16px 20px" }}>
                    <p style={{ margin:"0 0 12px", fontSize:11, fontWeight:700,
                      textTransform:"uppercase", letterSpacing:"0.12em",
                      color:C.inkLight, fontFamily:"'Inter',sans-serif" }}>
                      Booking Details
                    </p>
                    {[
                      ["👤 Name",  form.name],
                      ["📞 Phone", formatPhone(form.phone)],
                      form.appointmentDate && ["📅 Date",  form.appointmentDate],
                      form.type === "Appointment" && form.doctorProfileName && ["🩺 Doctor", `Dr. ${form.doctorProfileName}`],
                      isOnline && form.preferredTime && ["🕐 Time",  `${form.preferredTime} (${form.timezone})`],
                      isOnline && form.doctorName && ["🩺 Doctor", `Dr. ${form.doctorName}`],
                      isOnline && ["💳 Paid",  `₹${consultFee}`],
                    ].filter(Boolean).map(([k, v]) => (
                      <div key={k} style={{ display:"flex", justifyContent:"space-between",
                        alignItems:"center", padding:"7px 0",
                        borderBottom:`1px solid ${C.creamD}` }}>
                        <span style={{ fontSize:12, color:C.inkLight,
                          fontFamily:"'Inter',sans-serif" }}>{k}</span>
                        <span style={{ fontSize:13, fontWeight:600, color:C.ink,
                          fontFamily:"'Inter',sans-serif" }}>{v}</span>
                      </div>
                    ))}
                  </motion.div>

                  {/* ── Action buttons ── */}
                  <motion.div
                    initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                    transition={{ delay:0.6 }}
                    style={{ padding:"24px 32px 32px",
                      display:"flex", flexDirection:"column", gap:12 }}>

                    {/* Primary: track status */}
                    <motion.a
                      href={`/status?phone=${encodeURIComponent(formatPhone(form.phone))}`}
                      whileHover={{ scale:1.02, boxShadow:`0 14px 34px rgba(232,146,42,0.45)` }}
                      whileTap={{ scale:0.98 }}
                      style={{ display:"flex", alignItems:"center", justifyContent:"center",
                        gap:8, padding:"15px 0", borderRadius:14, textDecoration:"none",
                        background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                        color:"#fff", fontWeight:700, fontSize:15,
                        fontFamily:"'Inter',sans-serif",
                        boxShadow:`0 8px 26px rgba(232,146,42,0.35)` }}>
                      📋 Track My Appointment Status →
                    </motion.a>

                    {/* Secondary: book another */}
                    <motion.button
                      onClick={() => {
                        setSuccess(false);
                        setRefNumber("");
                        setStep(0);
                        setForm({ name:"", phone:{ countryCode:"+91", number:"" },
                          age:"", gender:"", type:"Appointment",
                          consultationType:"In-Person", doctorId:"", doctorName:"",
                          doctorProfileId:"", doctorProfileName:"",
                          appointmentDate:"", preferredTime:"",
                          timezone: detectedTZ, message:"" });
                        window.scrollTo({ top:0, behavior:"smooth" });
                      }}
                      whileHover={{ scale:1.01 }}
                      whileTap={{ scale:0.98 }}
                      style={{ padding:"13px 0", borderRadius:14,
                        border:`1.5px solid ${C.creamD}`, background:"transparent",
                        color:C.inkMid, fontWeight:600, fontSize:14,
                        cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      + Book Another Appointment
                    </motion.button>
                  </motion.div>

                </div>
              </motion.div>
            )}

          </AnimatePresence>

          {/* Check status link */}
          {step === 0 && (
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
              transition={{ delay:0.4 }}
              style={{ textAlign:"center", marginTop:18 }}>
              <p style={{ fontSize:13, color:C.inkLight,
                fontFamily:"'Inter',sans-serif" }}>
                Already submitted?{" "}
                <a href="/status" style={{ color:C.saffron, fontWeight:600,
                  textDecoration:"none" }}>
                  Check your status →
                </a>
              </p>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}

/* ─── Helper: load Razorpay checkout script ─── */
function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload  = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}